import { supabase } from '../lib/supabase'
import { 
  AvailabilityProfile, 
  DayAvailability, 
  SchedulingPreferences, 
  SmartSuggestion, 
  TimeSlot,
  AvailabilityCheck,
  BookingWindow,
  TimeOffPeriod
} from '../types/availability'
import { Shoot } from '../types'

class AvailabilityService {
  // Create default availability profile for new users
  async createDefaultProfile(userId: string): Promise<AvailabilityProfile> {
    // First check if user already has a profile
    try {
      const existingProfiles = await this.getProfiles(userId)
      if (existingProfiles.length > 0) {
        console.log('✅ User already has availability profiles')
        return existingProfiles.find(p => p.isDefault) || existingProfiles[0]
      }
    } catch (error) {
      console.log('Could not check existing profiles, proceeding with creation')
    }

    const defaultWorkingDays: DayAvailability[] = [
      // Sunday
      { dayOfWeek: 0, enabled: false, timeSlots: [] },
      // Monday - Friday
      ...Array.from({ length: 5 }, (_, i) => ({
        dayOfWeek: i + 1,
        enabled: true,
        timeSlots: [
          {
            id: `morning-${i + 1}`,
            start: '09:00',
            end: '12:00',
            available: true
          },
          {
            id: `afternoon-${i + 1}`,
            start: '13:00',
            end: '17:00',
            available: true
          }
        ],
        breakTimes: [
          {
            id: `lunch-${i + 1}`,
            start: '12:00',
            end: '13:00',
            available: false,
            reason: 'Lunch break'
          }
        ]
      })),
      // Saturday
      { 
        dayOfWeek: 6, 
        enabled: true, 
        timeSlots: [
          {
            id: 'saturday-morning',
            start: '10:00',
            end: '15:00',
            available: true
          }
        ]
      }
    ]

    const defaultProfile: Omit<AvailabilityProfile, 'id' | 'createdAt' | 'updatedAt'> = {
      userId,
      name: 'Default Working Hours',
      isDefault: true,
      workingDays: defaultWorkingDays,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      bufferBefore: 15,
      bufferAfter: 15,
      travelTime: 30,
      minBookingNotice: 24,
      maxBookingAdvance: 90
    }

    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('availability_profiles')
        .insert(defaultProfile)
        .select()
        .single()

      if (error) {
        console.error('Database error creating profile:', error)
        if (error.message.includes('relation "availability_profiles" does not exist')) {
          throw new Error('availability_profiles table does not exist. Please run the SQL migration.')
        }
        if (error.message.includes('foreign key constraint')) {
          throw new Error('Invalid user ID or users table constraint issue.')
        }
        throw new Error(`Database error: ${error.message}`)
      }
      
      console.log('✅ Default availability profile created successfully')
      return data
    } catch (error) {
      console.error('Error creating default availability profile:', error)
      throw error
    }
  }

  // Get all profiles for a user
  async getProfiles(userId: string): Promise<AvailabilityProfile[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('availability_profiles')
        .select('*')
        .eq('"userId"', userId)
        .order('"isDefault"', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching availability profiles:', error)
      return []
    }
  }

  // Update an availability profile
  async updateProfile(profileId: string, updates: Partial<AvailabilityProfile>): Promise<AvailabilityProfile> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('availability_profiles')
        .update(updates)
        .eq('id', profileId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating availability profile:', error)
      throw error
    }
  }

  // Get scheduling preferences for a user
  async getSchedulingPreferences(userId: string): Promise<SchedulingPreferences> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('scheduling_preferences')
        .select('*')
        .eq('"userId"', userId)
        .single()

      if (error && error.code === 'PGRST116') {
        // No preferences found, return default object (don't create automatically)
        console.log('No scheduling preferences found for user')
        return this.getDefaultPreferencesObject(userId)
      }

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching scheduling preferences:', error)
      return this.getDefaultPreferencesObject(userId)
    }
  }

  // Get default preferences object without database interaction
  private getDefaultPreferencesObject(userId: string): SchedulingPreferences {
    return {
      userId,
      defaultDuration: 120, // 2 hours
      allowBackToBack: false,
      requireBuffer: true,
      autoDeclineConflicts: true,
      workingHoursOnly: true,
      weekendBookings: true,
      holidayBookings: false,
      maxDailyBookings: 3,
      preferredStartTimes: ['09:00', '10:00', '14:00']
    }
  }

  // Create default scheduling preferences
  async createDefaultPreferences(userId: string): Promise<SchedulingPreferences> {
    const defaultPreferences: SchedulingPreferences = {
      userId,
      defaultDuration: 120, // 2 hours
      allowBackToBack: false,
      requireBuffer: true,
      autoDeclineConflicts: true,
      workingHoursOnly: true,
      weekendBookings: true,
      holidayBookings: false,
      maxDailyBookings: 3,
      preferredStartTimes: ['09:00', '10:00', '14:00']
    }

    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('scheduling_preferences')
        .insert(defaultPreferences)
        .select()
        .single()

      if (error) {
        console.error('Database error creating preferences:', error)
        if (error.message.includes('relation "scheduling_preferences" does not exist')) {
          throw new Error('scheduling_preferences table does not exist. Please run the SQL migration.')
        }
        if (error.message.includes('foreign key constraint')) {
          throw new Error('Invalid user ID or users table constraint issue.')
        }
        // If it's a duplicate key error, fetch existing preferences
        if (error.message.includes('duplicate key') || error.message.includes('unique constraint')) {
          console.log('User already has preferences, fetching existing ones...')
          try {
            const { data: existingData, error: fetchError } = await supabase
              .from('scheduling_preferences')
              .select('*')
              .eq('"userId"', userId)
              .single()
            
            if (fetchError) throw fetchError
            return existingData
          } catch (fetchError) {
            console.error('Error fetching existing preferences:', fetchError)
            return this.getDefaultPreferencesObject(userId)
          }
        }
        throw new Error(`Database error: ${error.message}`)
      }
      
      console.log('✅ Default scheduling preferences created successfully')
      return data
    } catch (error) {
      console.error('Error creating default scheduling preferences:', error)
      throw error
    }
  }

  // Update scheduling preferences
  async updatePreferences(userId: string, preferences: Partial<SchedulingPreferences>): Promise<SchedulingPreferences> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('scheduling_preferences')
        .update(preferences)
        .eq('"userId"', userId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating scheduling preferences:', error)
      throw error
    }
  }

  // TIME OFF METHODS

  // Get all time off periods for a user
  async getTimeOffPeriods(userId: string, startDate?: string, endDate?: string): Promise<TimeOffPeriod[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('time_off_periods')
        .select('*')
        .eq('"userId"', userId)
        .order('"startDate"', { ascending: true })

      if (startDate) {
        query = query.gte('"startDate"', startDate)
      }
      if (endDate) {
        query = query.lte('"endDate"', endDate)
      }

      const { data, error } = await query

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching time off periods:', error)
      return []
    }
  }

  // Create a new time off period
  async createTimeOffPeriod(timeOff: Omit<TimeOffPeriod, 'id' | 'createdAt' | 'updatedAt'>): Promise<TimeOffPeriod> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('time_off_periods')
        .insert(timeOff)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating time off period:', error)
      throw error
    }
  }

  // Update a time off period
  async updateTimeOffPeriod(id: string, updates: Partial<TimeOffPeriod>): Promise<TimeOffPeriod> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('time_off_periods')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating time off period:', error)
      throw error
    }
  }

  // Delete a time off period
  async deleteTimeOffPeriod(id: string): Promise<void> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('time_off_periods')
        .delete()
        .eq('id', id)

      if (error) throw error
    } catch (error) {
      console.error('Error deleting time off period:', error)
      throw error
    }
  }

  // Check if a date/time conflicts with time off
  async checkTimeOffConflicts(
    userId: string, 
    date: string, 
    startTime?: string, 
    endTime?: string
  ): Promise<TimeOffPeriod[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Check if availability tables exist first
      const tablesExist = await this.checkAvailabilityTablesExist()
      if (!tablesExist) return []

      const { data, error } = await supabase
        .rpc('check_time_off_conflict', {
          p_user_id: userId,
          p_date: date,
          p_start_time: startTime || null,
          p_end_time: endTime || null
        })

      if (error) {
        console.error('Time off conflict check error:', error)
        return []
      }

      // Convert RPC result to TimeOffPeriod objects
      const timeOffPeriods = await this.getTimeOffPeriods(userId, date, date)
      return timeOffPeriods.filter(period => 
        data?.some((conflict: any) => conflict.conflict_id === period.id)
      )
    } catch (error) {
      console.error('Error checking time off conflicts:', error)
      return []
    }
  }

  // Check availability for a specific date and time
  async checkAvailability(
    userId: string, 
    date: string, 
    startTime: string, 
    endTime: string,
    excludeShootId?: string
  ): Promise<AvailabilityCheck> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Check if availability tables exist
      const tablesExist = await this.checkAvailabilityTablesExist()
      
      if (!tablesExist) {
        console.log('Availability tables not found, using simple conflict checking')
        return await this.getSimpleAvailabilityCheck(userId, date, startTime, endTime, excludeShootId)
      }

      // Get user's availability profile
      const profiles = await this.getProfiles(userId)
      const profile = profiles.find(p => p.isDefault) || profiles[0]
      
      if (!profile) {
        console.log('No availability profile found, using simple conflict checking')
        return await this.getSimpleAvailabilityCheck(userId, date, startTime, endTime, excludeShootId)
      }

      // Check time off conflicts
      const timeOffConflicts = await this.checkTimeOffConflicts(userId, date, startTime, endTime)

      // Check day of week availability
      const requestDate = new Date(date + 'T00:00:00')
      const dayOfWeek = requestDate.getDay()
      const dayAvailability = profile.workingDays.find((d: any) => d.dayOfWeek === dayOfWeek)
      
      if (!dayAvailability?.enabled) {
        return {
          date,
          timeSlot: { id: `check-${Date.now()}`, start: startTime, end: endTime, available: false },
          available: false,
          conflicts: ['Not a working day'],
          suggestions: [],
          timeOffConflicts
        }
      }

      // Check if within working hours
      const withinWorkingHours = this.isWithinWorkingHours(dayAvailability, startTime, endTime)
      if (!withinWorkingHours) {
        return {
          date,
          timeSlot: { id: `check-${Date.now()}`, start: startTime, end: endTime, available: false },
          available: false,
          conflicts: ['Outside working hours'],
          suggestions: [],
          timeOffConflicts
        }
      }

      // Check for conflicts with existing bookings
      const conflicts = await this.getConflicts(userId, date, startTime, endTime, excludeShootId)
      
      // Check buffer time
      const bufferOk = await this.checkBufferTime(userId, date, startTime, endTime, profile)
      
      const allConflicts = []
      if (conflicts.length > 0) {
        allConflicts.push(`Conflicts with ${conflicts.length} existing booking(s)`)
      }
      if (!bufferOk) {
        allConflicts.push('Insufficient buffer time')
      }
      if (timeOffConflicts.length > 0) {
        allConflicts.push(`Conflicts with time off: ${timeOffConflicts.map(t => t.title).join(', ')}`)
      }

      const timeSlot: TimeSlot = {
        id: `check-${Date.now()}`,
        start: startTime,
        end: endTime,
        available: allConflicts.length === 0
      }

      const suggestions = allConflicts.length > 0 
        ? await this.generateAlternativeSuggestions(userId, date, this.getMinutesBetween(startTime, endTime))
        : []

      return {
        date,
        timeSlot,
        available: allConflicts.length === 0,
        conflicts: allConflicts,
        suggestions,
        timeOffConflicts
      }

    } catch (error) {
      console.error('Error checking availability:', error)
      return await this.getSimpleAvailabilityCheck(userId, date, startTime, endTime, excludeShootId)
    }
  }

  // Simple availability check without advanced features
  private async getSimpleAvailabilityCheck(
    userId: string, 
    date: string, 
    startTime: string, 
    endTime: string,
    excludeShootId?: string
  ): Promise<AvailabilityCheck> {
    try {
      const conflicts = await this.getConflicts(userId, date, startTime, endTime, excludeShootId)
      
      const timeSlot: TimeSlot = {
        id: `check-${Date.now()}`,
        start: startTime,
        end: endTime,
        available: conflicts.length === 0
      }

      const available = conflicts.length === 0
      const conflictReasons = available ? [] : [`Conflicts with ${conflicts.length} existing booking(s)`]

      return {
        date,
        timeSlot,
        available,
        conflicts: conflictReasons,
        suggestions: []
      }
    } catch (error) {
      console.error('Error in simple availability check:', error)
      return {
        date,
        timeSlot: { id: `error-${Date.now()}`, start: startTime, end: endTime, available: false },
        available: false,
        conflicts: ['Error checking availability'],
        suggestions: []
      }
    }
  }

  // Get smart scheduling suggestions
  async getSmartSuggestions(
    userId: string,
    preferredDate?: string,
    duration: number = 120,
    clientId?: string
  ): Promise<SmartSuggestion[]> {
    try {
      // Check if tables exist
      const tablesExist = await this.checkAvailabilityTablesExist()
      
      if (!tablesExist) {
        console.log('Using simple suggestions due to missing tables')
        return await this.getSimpleTimeSuggestions(userId, preferredDate, duration)
      }

      const [profiles, preferences] = await Promise.all([
        this.getProfiles(userId),
        this.getSchedulingPreferences(userId)
      ])

      const profile = profiles.find(p => p.isDefault) || profiles[0]
      if (!profile) {
        return await this.getSimpleTimeSuggestions(userId, preferredDate, duration)
      }

      const suggestions: SmartSuggestion[] = []
      const startDate = preferredDate ? new Date(preferredDate) : new Date()
      const dates = this.getDateRange(startDate, 14) // Next 2 weeks

      for (const date of dates) {
        const dayOfWeek = date.getDay()
        const dayAvailability = profile.workingDays.find(d => d.dayOfWeek === dayOfWeek)
        
        if (!dayAvailability?.enabled) continue

        // Check for time off conflicts
        const dateString = this.formatDate(date)
        const timeOffConflicts = await this.checkTimeOffConflicts(userId, dateString)
        if (timeOffConflicts.length > 0) continue

        const availableWindows = await this.getAvailableWindows(userId, date, duration)
        
        for (const window of availableWindows) {
          const confidence = this.calculateConfidence(window, preferences, date, clientId)
          
          suggestions.push({
            id: `suggestion-${date.toISOString()}-${window.start.getHours()}`,
            date: this.formatDate(date),
            startTime: this.formatTime(window.start),
            endTime: this.formatTime(window.end),
            confidence,
            reason: this.generateReasoningText(confidence, window, preferences),
            factors: {
              availabilityMatch: this.getAvailabilityScore(window, dayAvailability),
              workloadBalance: this.getWorkloadScore(date),
              clientPreference: clientId ? this.getClientPreferenceScore(clientId, window) : 70,
              travelOptimization: this.getTravelScore(userId, date, window),
              energyLevel: this.getEnergyScore(window)
            }
          })
        }
      }

      // Sort by confidence and return top suggestions
      return suggestions
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 10)

    } catch (error) {
      console.error('Error generating smart suggestions:', error)
      return await this.getSimpleTimeSuggestions(userId, preferredDate, duration)
    }
  }

  // Simple time suggestions when advanced features aren't available
  private async getSimpleTimeSuggestions(
    userId: string,
    preferredDate?: string,
    duration: number = 120
  ): Promise<SmartSuggestion[]> {
    try {
      const suggestions: SmartSuggestion[] = []
      const startDate = preferredDate ? new Date(preferredDate) : new Date()
      const dates = this.getDateRange(startDate, 7) // Next week

      for (const date of dates) {
        const dayOfWeek = date.getDay()
        
        // Skip weekends for simple suggestions
        if (dayOfWeek === 0 || dayOfWeek === 6) continue

        const dateString = this.formatDate(date)
        
        // Check for existing conflicts
        const conflicts = await this.getConflicts(userId, dateString, '09:00', '17:00')
        
        if (conflicts.length === 0) {
          // Generate morning and afternoon suggestions
          const morningTime = '10:00'
          const afternoonTime = '14:00'
          
          const morningEnd = this.addMinutes(morningTime, duration)
          const afternoonEnd = this.addMinutes(afternoonTime, duration)
          
          suggestions.push({
            id: `simple-morning-${date.toISOString()}`,
            date: dateString,
            startTime: morningTime,
            endTime: morningEnd,
            confidence: 85,
            reason: 'Available morning slot',
            factors: {
              availabilityMatch: 85,
              workloadBalance: 80,
              clientPreference: 75,
              travelOptimization: 80,
              energyLevel: 90
            }
          })
          
          if (afternoonEnd <= '17:00') {
            suggestions.push({
              id: `simple-afternoon-${date.toISOString()}`,
              date: dateString,
              startTime: afternoonTime,
              endTime: afternoonEnd,
              confidence: 80,
              reason: 'Available afternoon slot',
              factors: {
                availabilityMatch: 80,
                workloadBalance: 85,
                clientPreference: 75,
                travelOptimization: 80,
                energyLevel: 85
              }
            })
          }
        }
      }
      
      return suggestions.slice(0, 5)
    } catch (error) {
      console.error('Error generating simple suggestions:', error)
      return []
    }
  }

  // Check if availability tables exist with multiple verification methods
  async checkAvailabilityTablesExist(): Promise<boolean> {
    try {
      if (!supabase) {
        console.log('Supabase not configured')
        return false
      }

      console.log('Starting comprehensive availability tables check...')

      // Method 1: Try to query the tables directly with a simple query
      try {
        const results = await Promise.allSettled([
          supabase.from('availability_profiles').select('id').limit(1),
          supabase.from('scheduling_preferences').select('id').limit(1),
          supabase.from('time_off_periods').select('id').limit(1),
          supabase.from('smart_suggestions').select('id').limit(1)
        ])

        console.log('Table query results:', results.map((result, index) => ({
          table: ['availability_profiles', 'scheduling_preferences', 'time_off_periods', 'smart_suggestions'][index],
          status: result.status,
          error: result.status === 'rejected' ? result.reason : 
                 result.status === 'fulfilled' && result.value.error ? result.value.error.message : null
        })))

        // Check if all tables are accessible
        const allTablesExist = results.every(result => 
          result.status === 'fulfilled' && !result.value.error
        )

        if (allTablesExist) {
          console.log('✅ All availability tables exist and are accessible')
          
          // Method 2: Test schema by trying to select camelCase columns
          try {
            console.log('🔍 Verifying table schema...')
            const schemaTests = await Promise.allSettled([
              supabase.from('availability_profiles').select('"userId", "isDefault", "workingDays"').limit(1),
              supabase.from('scheduling_preferences').select('"userId", "defaultDuration", "preferredStartTimes"').limit(1),
              supabase.from('time_off_periods').select('"userId", "startDate", "endDate"').limit(1)
            ])

            const schemaValid = schemaTests.every(result => 
              result.status === 'fulfilled' && !result.value.error
            )

            if (schemaValid) {
              console.log('✅ Table schema is correct (camelCase columns)')
              return true
            } else {
              console.log('❌ Table schema appears incorrect (missing camelCase columns)')
              console.log('Schema test results:', schemaTests.map((result, index) => ({
                table: ['availability_profiles', 'scheduling_preferences', 'time_off_periods'][index],
                status: result.status,
                error: result.status === 'rejected' ? result.reason : 
                       result.status === 'fulfilled' && result.value.error ? result.value.error.message : null
              })))
              return false
            }
          } catch (schemaError) {
            console.log('❌ Schema verification failed:', schemaError)
            return false
          }
        }

        console.log('❌ Some tables are missing or inaccessible')
      } catch (directError) {
        console.log('Direct query failed:', directError)
      }

      return false

    } catch (error) {
      console.error('Error in checkAvailabilityTablesExist:', error)
      return false
    }
  }

  // Helper methods
  private async getDefaultProfile(userId: string): Promise<AvailabilityProfile> {
    const profiles = await this.getProfiles(userId)
    let defaultProfile = profiles.find(p => p.isDefault)
    
    if (!defaultProfile) {
      defaultProfile = await this.createDefaultProfile(userId)
    }
    
    return defaultProfile
  }

  // Get available booking windows for a specific date
  async getAvailableWindows(userId: string, date: Date, duration: number): Promise<BookingWindow[]> {
    try {
      const profile = await this.getDefaultProfile(userId)
      const dayOfWeek = date.getDay()
      const dayAvailability = profile.workingDays.find(d => d.dayOfWeek === dayOfWeek)
      
      if (!dayAvailability?.enabled) return []

      const windows: BookingWindow[] = []
      const dateString = this.formatDate(date)
      
      // Get existing bookings for this date
      const existingBookings = await this.getExistingBookings(userId, dateString)
      
      for (const timeSlot of dayAvailability.timeSlots) {
        if (!timeSlot.available) continue
        
        const slotStart = this.parseTime(timeSlot.start)
        const slotEnd = this.parseTime(timeSlot.end)
        
        // Find available windows within this time slot
        const slotWindows = this.findAvailableWindows(
          slotStart, 
          slotEnd, 
          existingBookings, 
          duration, 
          profile.bufferBefore + profile.bufferAfter
        )
        
        windows.push(...slotWindows)
      }
      
      return windows
    } catch (error) {
      console.error('Error getting available windows:', error)
      return []
    }
  }

  private async getConflicts(
    userId: string, 
    date: string, 
    startTime: string, 
    endTime: string,
    excludeShootId?: string
  ): Promise<Shoot[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('shoots')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .neq('status', 'cancelled')

      if (excludeShootId) {
        query = query.neq('id', excludeShootId)
      }

      const { data, error } = await query
      if (error) throw error

      return (data || []).filter(shoot => {
        return startTime < shoot.end_time && shoot.start_time < endTime
      })
    } catch (error) {
      console.error('Error getting conflicts:', error)
      return []
    }
  }

  private isWithinWorkingHours(dayAvailability: DayAvailability, startTime: string, endTime: string): boolean {
    for (const timeSlot of dayAvailability.timeSlots) {
      if (timeSlot.available && startTime >= timeSlot.start && endTime <= timeSlot.end) {
        return true
      }
    }
    return false
  }

  private async checkBufferTime(
    userId: string, 
    date: string, 
    startTime: string, 
    endTime: string, 
    profile: AvailabilityProfile
  ): Promise<boolean> {
    const conflicts = await this.getConflicts(userId, date, 
      this.subtractMinutes(startTime, profile.bufferBefore),
      this.addMinutes(endTime, profile.bufferAfter)
    )
    return conflicts.length === 0
  }

  private async generateAlternativeSuggestions(
    userId: string, 
    originalDate: string, 
    duration: number
  ): Promise<SmartSuggestion[]> {
    const date = new Date(originalDate + 'T00:00:00')
    const suggestions: SmartSuggestion[] = []
    
    // Try same day, different times
    const windows = await this.getAvailableWindows(userId, date, duration)
    for (const window of windows.slice(0, 3)) {
      suggestions.push({
        id: `alt-${Date.now()}-${window.start.getTime()}`,
        date: originalDate,
        startTime: this.formatTime(window.start),
        endTime: this.formatTime(window.end),
        confidence: 85,
        reason: 'Alternative time on same day',
        factors: {
          availabilityMatch: 90,
          workloadBalance: 80,
          clientPreference: 80,
          travelOptimization: 90,
          energyLevel: 85
        }
      })
    }
    
    // Try next few days
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(date)
      nextDate.setDate(date.getDate() + i)
      
      const nextWindows = await this.getAvailableWindows(userId, nextDate, duration)
      if (nextWindows.length > 0) {
        const bestWindow = nextWindows[0]
        suggestions.push({
          id: `alt-${Date.now()}-${nextDate.getTime()}`,
          date: this.formatDate(nextDate),
          startTime: this.formatTime(bestWindow.start),
          endTime: this.formatTime(bestWindow.end),
          confidence: 75 - (i * 5),
          reason: `Available ${i} day${i > 1 ? 's' : ''} later`,
          factors: {
            availabilityMatch: 85,
            workloadBalance: 85,
            clientPreference: 70 - (i * 5),
            travelOptimization: 80,
            energyLevel: 80
          }
        })
      }
    }
    
    return suggestions.slice(0, 5)
  }

  // Utility methods for time calculations and formatting
  private getDateRange(startDate: Date, days: number): Date[] {
    const dates: Date[] = []
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  private formatTime(date: Date): string {
    return date.toTimeString().slice(0, 5)
  }

  private parseTime(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    return date
  }

  private subtractMinutes(timeString: string, minutes: number): string {
    const date = this.parseTime(timeString)
    date.setMinutes(date.getMinutes() - minutes)
    return this.formatTime(date)
  }

  private addMinutes(timeString: string, minutes: number): string {
    const date = this.parseTime(timeString)
    date.setMinutes(date.getMinutes() + minutes)
    return this.formatTime(date)
  }

  private calculateConfidence(window: BookingWindow, preferences: SchedulingPreferences, date: Date, _clientId?: string): number {
    let confidence = 70 // base confidence
    
    // Preferred time bonus
    const timeString = this.formatTime(window.start)
    if (preferences.preferredStartTimes.includes(timeString)) {
      confidence += 15
    }
    
    // Workload balance
    const dayOfWeek = date.getDay()
    if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Weekday
      confidence += 10
    }
    
    // Not too early or late
    const hour = window.start.getHours()
    if (hour >= 9 && hour <= 15) {
      confidence += 10
    }
    
    return Math.min(confidence, 100)
  }

  private generateReasoningText(confidence: number, _window: BookingWindow, _preferences: SchedulingPreferences): string {
    if (confidence >= 90) {
      return 'Optimal time slot matching your preferences'
    } else if (confidence >= 75) {
      return 'Good time slot with minor considerations'
    } else if (confidence >= 60) {
      return 'Available slot with some scheduling constraints'
    } else {
      return 'Available but not ideal timing'
    }
  }

  private getAvailabilityScore(_window: BookingWindow, _dayAvailability: DayAvailability): number {
    // Score based on how well the window fits within preferred time slots
    return 85 // Simplified for now
  }

  private getWorkloadScore(_date: Date): number {
    // Score based on existing workload for the day
    return 80 // Simplified for now
  }

  private getClientPreferenceScore(_clientId: string, _window: BookingWindow): number {
    // Score based on client's historical preferences
    return 75 // Simplified for now
  }

  private getTravelScore(_userId: string, _date: Date, _window: BookingWindow): number {
    // Score based on travel time optimization
    return 80 // Simplified for now
  }

  private getEnergyScore(window: BookingWindow): number {
    // Score based on optimal energy levels throughout the day
    const hour = window.start.getHours()
    if (hour >= 9 && hour <= 11) return 95 // Morning peak
    if (hour >= 14 && hour <= 16) return 90 // Afternoon peak
    if (hour >= 11 && hour <= 14) return 80 // Pre-lunch
    return 70 // Other times
  }

  private async getExistingBookings(userId: string, date: string): Promise<BookingWindow[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('shoots')
        .select('start_time, end_time, title')
        .eq('user_id', userId)
        .eq('date', date)
        .neq('status', 'cancelled')

      if (error) throw error

      return (data || []).map(booking => ({
        start: this.parseTimeWithDate(date, booking.start_time),
        end: this.parseTimeWithDate(date, booking.end_time),
        duration: this.getMinutesBetween(booking.start_time, booking.end_time),
        type: 'busy' as const,
        title: booking.title
      }))
    } catch (error) {
      console.error('Error getting existing bookings:', error)
      return []
    }
  }

  private parseTimeWithDate(date: string, time: string): Date {
    return new Date(`${date}T${time}:00`)
  }

  private getMinutesBetween(startTime: string, endTime: string): number {
    const start = this.parseTime(startTime)
    const end = this.parseTime(endTime)
    return (end.getTime() - start.getTime()) / (1000 * 60)
  }

  private findAvailableWindows(
    slotStart: Date,
    slotEnd: Date,
    existingBookings: BookingWindow[],
    duration: number,
    bufferTime: number
  ): BookingWindow[] {
    const windows: BookingWindow[] = []
    let currentTime = new Date(slotStart)
    
    // Sort existing bookings by start time
    const sortedBookings = existingBookings
      .filter(b => b.start >= slotStart && b.end <= slotEnd)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
    
    for (const booking of sortedBookings) {
      const availableTime = booking.start.getTime() - currentTime.getTime()
      const requiredTime = (duration + bufferTime) * 60 * 1000
      
      if (availableTime >= requiredTime) {
        const windowEnd = new Date(currentTime.getTime() + duration * 60 * 1000)
        windows.push({
          start: new Date(currentTime),
          end: windowEnd,
          duration,
          type: 'available'
        })
      }
      
      currentTime = new Date(booking.end.getTime() + bufferTime * 60 * 1000)
    }
    
    // Check for available time after last booking
    const remainingTime = slotEnd.getTime() - currentTime.getTime()
    const requiredTime = duration * 60 * 1000
    
    if (remainingTime >= requiredTime) {
      const windowEnd = new Date(currentTime.getTime() + duration * 60 * 1000)
      if (windowEnd <= slotEnd) {
        windows.push({
          start: new Date(currentTime),
          end: windowEnd,
          duration,
          type: 'available'
        })
      }
    }
    
    return windows
  }

  // Setup availability system for a user (creates tables if needed and default profile)
  async setupAvailabilitySystem(userId: string, force: boolean = false): Promise<{ success: boolean; message: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Check if tables exist (unless forced)
      if (!force) {
        const tablesExist = await this.checkAvailabilityTablesExist()
        
        if (!tablesExist) {
          return {
            success: false,
            message: 'Availability tables need to be created. Please run the availability migration in your Supabase dashboard.'
          }
        }
      } else {
        console.log('Force mode: skipping table existence check')
      }

      // Check if user already has a profile
      try {
        const existingProfiles = await this.getProfiles(userId)
        if (existingProfiles.length > 0) {
          return {
            success: true,
            message: 'Availability system already set up'
          }
        }
      } catch (profileCheckError) {
        if (!force) {
          console.error('Error checking existing profiles:', profileCheckError)
          return {
            success: false,
            message: 'Failed to check existing profiles. Tables may not exist.'
          }
        }
        console.log('Force mode: ignoring profile check error')
      }

      // Create default profile and preferences
      try {
        await Promise.all([
          this.createDefaultProfile(userId),
          this.createDefaultPreferences(userId)
        ])

        return {
          success: true,
          message: 'Availability system set up successfully'
        }
      } catch (createError) {
        console.error('Error creating default profile/preferences:', createError)
        return {
          success: false,
          message: `Failed to create availability profile: ${createError instanceof Error ? createError.message : 'Unknown error'}`
        }
      }
    } catch (error) {
      console.error('Error setting up availability system:', error)
      return {
        success: false,
        message: `Setup failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      }
    }
  }

  // Force setup availability system for a user (bypasses all checks)
  async forceSetupAvailabilitySystem(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      console.log('🚀 Force setting up availability system for user:', userId)

      // Try to create default profile and preferences directly
      try {
        await Promise.all([
          this.createDefaultProfile(userId),
          this.createDefaultPreferences(userId)
        ])

        console.log('✅ Force setup completed successfully')
        return {
          success: true,
          message: 'Availability system set up successfully (forced)'
        }
      } catch (createError) {
        console.error('Error in force setup:', createError)
        
        // If the error is about existing profiles, that's actually success
        if (createError instanceof Error && createError.message.includes('duplicate key')) {
          return {
            success: true,
            message: 'Availability system already set up'
          }
        }
        
        return {
          success: false,
          message: `Force setup failed: ${createError instanceof Error ? createError.message : 'Unknown error'}`
        }
      }
    } catch (error) {
      console.error('Error in forceSetupAvailabilitySystem:', error)
      return {
        success: false,
        message: `Force setup failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      }
    }
  }
}

export const availabilityService = new AvailabilityService() 